import React, { CSSProperties } from "react";
import Image from "next/image";
import { getImageDimensions } from "@sanity/asset-utils";
import { urlFor } from "@/lib/image";
import { sanitizeString } from "@/lib/string";
import { buildSrc, calculateCroppedDimensions } from "./utils";
import type { SanityImageProps } from "./types";
import { isImageAsset } from "./types";
import { getAspectRatioClassName } from "./aspectRatios";
import clsx from "clsx";
import styles from "./SanityImage.module.scss";

/**
 * Server-side Sanity Image Component
 *
 * Features:
 * - Responsive image support with srcset
 * - Automatic crop and hotspot handling
 * - LQIP (Low Quality Image Placeholder) support
 * - Optimized for Core Web Vitals
 * - SEO-friendly with proper alt text
 */
const SanityImage: React.FC<SanityImageProps> = ({
  image,
  alt,
  width,
  height,
  fit,
  sizes,
  quality = 75,
  priority = false,
  className,
  style: styleProp,
  fill,
  aspectRatio,
  ...props
}) => {
  // Early return if no image or if asset is not dereferenced
  if (!image?.asset || !isImageAsset(image.asset)) return null;

  const { hotspot, crop, asset } = image;

  // Smart default: when using fill, default to "crop" (cover behavior)
  // When using fixed dimensions, default to "original" (maintain aspect ratio)
  const effectiveFit = fit ?? (fill ? "crop" : "original");
  const sanityFit = effectiveFit === "crop" ? "max" : "clip";

  // Get image dimensions
  const imageDimensions = getImageDimensions(asset);
  const { dimensions, lqip } = asset.metadata ?? {};

  // Calculate cropped dimensions
  const croppedDimensions = calculateCroppedDimensions(
    dimensions?.width ?? imageDimensions.width,
    dimensions?.height ?? imageDimensions.height,
    crop
  );

  // Calculate hotspot-based object position
  const style: CSSProperties = {
    ...styleProp
  };

  if (hotspot && effectiveFit === "crop") {
    style.objectPosition = [hotspot.x, hotspot.y]
      .map((value) => ((value ?? 0.5) * 100).toFixed(2) + "%")
      .join(" ");
  }

  // Build responsive srcset
  const builder = urlFor(image);

  // If fill mode, use responsive srcset
  if (fill) {
    // Use the actual cropped dimensions instead of hardcoded 1920x1080.
    // The custom loader will override width for each srcset entry,
    // and remove height so Sanity calculates it proportionally.
    const src = buildSrc(builder, asset, {
      width: croppedDimensions.width,
      height: croppedDimensions.height,
      fit: sanityFit,
      quality
    });

    const aspectRatioClass = aspectRatio
      ? getAspectRatioClassName(aspectRatio)
      : "";

    // If aspectRatio is provided, wrap in container with aspect ratio
    if (aspectRatio && aspectRatio !== "auto") {
      return (
        <div
          className={clsx(
            styles.imageContainer,
            styles[aspectRatioClass],
            className
          )}
        >
          <Image
            src={src}
            alt={sanitizeString(alt) || ""}
            fill
            sizes={sizes}
            quality={quality}
            priority={priority}
            placeholder={lqip ? "blur" : undefined}
            blurDataURL={lqip}
            className={clsx({
              [styles.imageFitCover]: effectiveFit === "crop",
              [styles.imageFitContain]: effectiveFit === "original"
            })}
            style={style}
            {...props}
          />
        </div>
      );
    }

    // Without aspectRatio, return bare Image (user must provide container)
    return (
      <Image
        src={src}
        alt={sanitizeString(alt) || ""}
        fill
        sizes={sizes}
        quality={quality}
        priority={priority}
        placeholder={lqip ? "blur" : undefined}
        blurDataURL={lqip}
        className={clsx(className, {
          [styles.imageFitCover]: effectiveFit === "crop",
          [styles.imageFitContain]: effectiveFit === "original"
        })}
        style={style}
        {...props}
      />
    );
  }

  // Build src for specific dimensions
  const finalWidth =
    typeof width === "number" ? width : croppedDimensions.width;
  const finalHeight =
    typeof height === "number" ? height : croppedDimensions.height;

  const src = buildSrc(builder, asset, {
    width: finalWidth,
    height: finalHeight,
    fit: sanityFit,
    quality
  });

  return (
    <Image
      src={src}
      alt={sanitizeString(alt) || ""}
      width={finalWidth}
      height={finalHeight}
      sizes={sizes}
      quality={quality}
      priority={priority}
      placeholder={lqip ? "blur" : undefined}
      blurDataURL={lqip}
      className={clsx(className, {
        [styles.imageFitCover]: effectiveFit === "crop",
        [styles.imageFitContain]: effectiveFit === "original"
      })}
      style={style}
      {...props}
    />
  );
};

export default SanityImage;
